import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { APIURL } from '../../../config';
import { Autocomplete, Box, Chip, FormLabel, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

const CHIP_COLOR = '#1976d2';

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
  bgcolor: selected ? color : 'transparent',
  color: selected ? '#fff' : color,
  border: `1px solid ${color}`,
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

const PersonalDetailsCard = ({
  formVals,
  customerList,
  edit,
  onRadioChangeHandler,
  formChangeHandler,
  deletePhoneNumber,
  handlePhoneListChange,
  onDeleteAddress,
  handleAddressListChange,
  addressList,
  customerType,
  getFieldLabel,
  isFieldRequired,
  isFieldVisible,
  errors = {},
}) => {
  const [selectedType, setSelectedType] = useState(formVals.cus_is_company ? 'Organization' : 'Individual');
  const isIndividual = selectedType !== 'Organization';

  const formatAddressDisplay = (a) => [a.address_line_1, a.address_line_2, a.city, a.state, a.zip].filter(Boolean).join(', ');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressInputValues, setAddressInputValues] = useState(addressList.map((a) => formatAddressDisplay(a)));
  const addressDebounceTimeouts = useRef({});

  useEffect(() => {
    setAddressInputValues(addressList.map((a) => formatAddressDisplay(a)));
    setAddressSuggestions([]);
  }, [addressList]);

  const handleTypeSelect = (val) => {
    if (val === selectedType) return;
    setSelectedType(val);
    onRadioChangeHandler(val);
    formChangeHandler({ target: { id: 'cus_is_company', value: val !== 'Individual' } });
  };

  const handleAddressInputChange = (idx, input) => {
    if (addressDebounceTimeouts.current[idx]) clearTimeout(addressDebounceTimeouts.current[idx]);
    addressDebounceTimeouts.current[idx] = setTimeout(async () => {
      try {
        const res = await axios.get(APIURL + '/maps', { params: { input } });
        setAddressSuggestions(res.data.predictions || []);
      } catch (e) {
        setAddressSuggestions([]);
      }
    }, 300);
    setAddressInputValues((prev) => {
      const newValues = [...prev];
      newValues[idx] = input;
      return newValues;
    });
  };

  const handleAddressSelect = async (idx, option) => {
    const res = await axios.get(APIURL + '/maps/details', { params: { place_id: option.place_id } });
    handleAddressListChange(idx, { target: { id: 'address_maps' }, value: res.data });
  };

  const duplicateDDD = formVals.DDDID && !!(customerList && customerList.find((entry) => entry.ddd_id === formVals.DDDID));

  return (
    <Stack spacing={2}>
      {!edit && (
        <Box>
          <FormLabel component="legend" sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
            Customer Type
          </FormLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {customerType.map((type) => (
              <Chip
                key={type}
                label={type}
                onClick={() => handleTypeSelect(type)}
                sx={selectableChipSx(selectedType === type)}
              />
            ))}
          </Box>
        </Box>
      )}

      {isIndividual ? (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {isFieldVisible('first_name') && (
            <TextField
              sx={{ flex: 1 }}
              size="small"
              variant="outlined"
              label={getFieldLabel('first_name', 'First Name')}
              id="first_name"
              value={formVals.first_name || ''}
              onChange={formChangeHandler}
              required
              error={!!errors.first_name}
              inputProps={{ maxLength: 25 }}
            />
          )}
          {isFieldVisible('last_name') && (
            <TextField
              sx={{ flex: 1 }}
              size="small"
              variant="outlined"
              label={getFieldLabel('last_name', 'Last Name')}
              id="last_name"
              value={formVals.last_name || ''}
              onChange={formChangeHandler}
              required
              error={!!errors.last_name}
              inputProps={{ maxLength: 25 }}
            />
          )}
        </Box>
      ) : (
        isFieldVisible('company_name') && (
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label={getFieldLabel('company_name', 'Company Name')}
            id="company_name"
            value={formVals.company_name || ''}
            onChange={formChangeHandler}
            required
            error={!!errors.company_name}
          />
        )
      )}

      {(isFieldVisible('DDDID') || isFieldVisible('dob')) && (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {isFieldVisible('DDDID') && (
            <TextField
              sx={{ flex: 1 }}
              size="small"
              variant="outlined"
              label={getFieldLabel('DDDID', 'DDDID')}
              id="DDDID"
              value={formVals.DDDID || ''}
              onChange={formChangeHandler}
              onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10); }}
              inputProps={{ maxLength: 10 }}
              required={isFieldRequired('DDDID')}
              error={!!errors.DDDID || duplicateDDD}
              helperText={duplicateDDD ? 'Already Exist.' : ''}
            />
          )}
          {isFieldVisible('dob') && (
            <Box sx={{ flex: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={getFieldLabel('dob', isIndividual ? 'Date of Birth' : 'Incorporation Date')}
                  value={formVals.dob ? dayjs(formVals.dob.replace(/T.*$/, '')) : null}
                  onChange={(d) => { if (d && d.isValid()) formChangeHandler({ target: { id: 'dob', value: d.format('YYYY-MM-DD') } }); }}
                  openTo="year"
                  views={['year', 'month', 'day']}
                  slotProps={{ textField: { id: 'dob', size: 'small', required: isIndividual, fullWidth: true, error: !!errors.dob } }}
                />
              </LocalizationProvider>
            </Box>
          )}
        </Box>
      )}

      {isFieldVisible('gender') && isIndividual && (
        <Box>
          <FormLabel component="legend" required error={!!errors.gender} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
            {getFieldLabel('gender', 'Gender')}
          </FormLabel>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {GENDER_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                onClick={() => formChangeHandler({ target: { id: 'gender', value: opt.value } })}
                sx={selectableChipSx(formVals.gender === opt.value)}
              />
            ))}
          </Box>
        </Box>
      )}

      {isFieldVisible('cus_local_id') && (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label={getFieldLabel('cus_local_id', 'Local ID')}
          id="cus_local_id"
          value={formVals.cus_local_id || ''}
          onChange={formChangeHandler}
          required={isFieldRequired('cus_local_id')}
          error={!!errors.cus_local_id}
        />
      )}

      {isFieldVisible('email') && (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label={getFieldLabel('email', 'E-mail')}
          id="email"
          value={formVals.email || ''}
          onChange={formChangeHandler}
          required
          error={!!errors.email}
        />
      )}

      {isFieldVisible('address') && (
        <Box>
          <FormLabel component="legend" required error={!!errors.address} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
            {getFieldLabel('address', 'Address')}
          </FormLabel>
          <Stack spacing={1.5}>
            {(addressList.length > 0 ? addressList : [{}]).map((add, idx) => (
              <Autocomplete
                key={idx}
                freeSolo
                size="small"
                options={addressSuggestions}
                getOptionLabel={(opt) => opt.description || ''}
                inputValue={addressInputValues[idx] || ''}
                onInputChange={(e, v) => handleAddressInputChange(idx, v)}
                onChange={(e, v, r) => { if (r === 'selectOption') handleAddressSelect(idx, v); }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    error={!!errors.address && !add.address_line_1}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {addressList.length > 1 && (
                            <InputAdornment position="end">
                              <IconButton aria-label="delete address" onClick={() => onDeleteAddress(idx)} edge="end" sx={{ color: '#d32f2f' }}>
                                <DeleteOutlineOutlinedIcon />
                              </IconButton>
                            </InputAdornment>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ))}
          </Stack>
        </Box>
      )}

      {isFieldVisible('mailing_detail') && (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label={getFieldLabel('mailing_detail', 'Additional address details, if any')}
          id="mailing_detail"
          value={formVals.mailing_detail || ''}
          onChange={formChangeHandler}
          multiline
          minRows={2}
        />
      )}

      {isFieldVisible('phone') && (
        <Box>
          <FormLabel component="legend" required error={!!errors.phone} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
            {getFieldLabel('phone', 'Phone')}
          </FormLabel>
          <Stack spacing={1.5}>
            {formVals.phnNumList.map((phn) => (
              <TextField
                key={phn.phnId}
                fullWidth
                size="small"
                variant="outlined"
                label={getFieldLabel('phone', 'Phone')}
                value={phn.phone}
                onChange={(e) => handlePhoneListChange(phn.phnId, e)}
                error={!!errors.phone}
                inputProps={{ maxLength: 100 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{phn.country_code}</InputAdornment>,
                  endAdornment: formVals.phnNumList.length > 1 && (
                    <InputAdornment position="end">
                      <IconButton aria-label="delete phone" onClick={() => deletePhoneNumber(phn.phnId)} edge="end" sx={{ color: '#d32f2f' }}>
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {!!errors.address && (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block' }}>
          Address is required
        </Typography>
      )}
    </Stack>
  );
};

export default PersonalDetailsCard;
